import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import AppinesBanner from './components/AppinesBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import ActivityDetails from './components/ActivityDetails';
import RemunerationIndiciaire from './components/RemunerationIndiciaire';
import PrimesIndemnites from './components/PrimesIndemnites';
import RevenueCalculator from './components/RevenueCalculator';
import EmbedHeader from './components/EmbedHeader';
import SEOStructuredData from './components/SEOStructuredData';
import LoadingSpinner from './components/ui/LoadingSpinner';
import WizardStepper from './components/wizard/WizardStepper';
import WizardNavigation from './components/wizard/WizardNavigation';
import EstimationPreview from './components/wizard/EstimationPreview';
import AppinesHiringBonus from './components/AppinesHiringBonus';
import Footer from './components/Footer';
import { useWizardSession } from './hooks/useWizardSession';
import { ActivityData, StatusData, PrimesData, Profession } from './types';
import type { OverseasTerritoryId } from './types';
import { parseUrlParams, getSimulatorPath } from './data/professions';
import { findOverseasTerritoryById } from './types';
import { useUserInteractions } from './hooks/useUserInteractions';
import { useUrlParams } from './hooks/useUrlParams';
import { usePrimesData } from './hooks/usePrimesData';
import { useCotisationsData } from './hooks/useCotisationsData';
import { useGrilleData } from './hooks/useGrilleHopitalIDE';
import { calculateSalary } from './utils/salaryCalculations';
import { initializeGoogleAnalytics, disableGoogleAnalytics, trackSimulatorInteraction } from './utils/googleAnalytics';
import {
  trackSimulatorLoaded,
  trackCookieConsent,
  trackWizardStepViewed,
  trackWizardStepCompleted,
  trackWizardBackClicked,
  trackProfessionSelected,
  trackTerritorySelected,
  trackGradeSelected,
  trackEchelonSelected,
  trackEstimationViewed,
  trackPrimeToggled,
  trackStatusSelected,
} from './utils/analyticsEvents';

// Lazy load non-critical components for better performance
const ArticlesCarousel = React.lazy(() => import('./components/ArticlesCarrousel'));
const ProfessionContextualContent = React.lazy(() => import('./components/ProfessionContextualContent'));
const SalaryComparisonTable = React.lazy(() => import('./components/SalaryComparisonTable'));
const ProfessionFAQ = React.lazy(() => import('./components/ProfessionFAQ'));

/**
 * @fileoverview Main application component for the salary simulator
 * 
 * This component is rendered by AppRouter for each route and uses useParams
 * to determine which profession to display. It manages all the application state
 * and coordinates between different sections of the simulator.
 * 
 * @author App'Ines Team
 * @version 2.0.0
 * @since 2025
 */

export default function App() {
  const { professionId, territoryId, gradeId, echelonId } = useParams<{ 
    professionId: string; 
    territoryId: string; 
    gradeId: string; 
    echelonId: string; 
  }>();
  const navigate = useNavigate();
  const { urlParams, isEmbedMode } = useUrlParams();
  const { userInteractions, markActivityModified, markPrimesModified } = useUserInteractions();
  const { primes: supabasePrimes, isLoadingPrimes, setIsLoadingPrimes, errorPrimes, isUsingFallbackPrimes } = usePrimesData();
  const { cotisations, isLoadingCotisations, errorCotisations, isUsingFallbackCotisations } = useCotisationsData();

  // Parse all URL parameters to get initial state
  const initialUrlState = useMemo(() => {
    console.log('🔍 App - Initial URL state calculation, params:', { professionId, territoryId, gradeId, echelonId });
    const { profession, territoryId: parsedTerritoryId, gradeId: parsedGradeId, parsedEchelon } = parseUrlParams(
      professionId, 
      territoryId, 
      gradeId, 
      echelonId
    );
    console.log('🔍 App - Initial URL state result:', { 
      profession: profession.id, 
      territoryId: parsedTerritoryId, 
      gradeId: parsedGradeId, 
      echelon: parsedEchelon 
    });
    return { profession, territoryId: parsedTerritoryId, gradeId: parsedGradeId, parsedEchelon };
  }, [professionId, territoryId, gradeId, echelonId]);

  // Initialize state from URL parameters
  const [selectedProfession, setSelectedProfession] = useState(() => initialUrlState.profession);
  const [selectedOverseasTerritory, setSelectedOverseasTerritory] = useState<OverseasTerritoryId | undefined>(() => initialUrlState.territoryId);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(() => initialUrlState.gradeId);
  const [selectedEchelon, setSelectedEchelon] = useState<number | null>(() => initialUrlState.parsedEchelon.echelonNum);

  const { grades } = useGrilleData(selectedProfession);

  const [workTimePercentage, setWorkTimePercentage] = useState<number>(100);
  const [primesData, setPrimesData] = useState<PrimesData>({
    primes: [],
    totalMensuel: 0
  });

  // États pour l'interface - déclarés après les états principaux
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);
  const [isSeoContextOpen, setIsSeoContextOpen] = useState(false);

  // État du wizard
  const WIZARD_STEPS = [
    { id: 0, label: 'Activité' },
    { id: 1, label: 'Grille indiciaire' },
    { id: 2, label: 'Primes & indemnités' },
    { id: 3, label: 'Résultats' },
  ];
  const [currentStep, setCurrentStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const stepEnteredAtRef = React.useRef<number>(Date.now());
  const wizardSectionRef = React.useRef<HTMLElement | null>(null);

  const scrollToWizard = () => {
    if (typeof window === 'undefined') return;
    if (wizardSectionRef.current) {
      wizardSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    trackWizardStepViewed(currentStep, WIZARD_STEPS[currentStep]?.label ?? String(currentStep));
    stepEnteredAtRef.current = Date.now();
    if (currentStep === 3) {
      trackEstimationViewed({
        profession: selectedProfession.id,
        grade: selectedGrade,
        echelon: selectedEchelon,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // État pour la détection mobile
  const [isMobile, setIsMobile] = useState(false);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Vérification initiale
    checkMobile();

    // Écouter les changements de taille d'écran
    window.addEventListener('resize', checkMobile);

    // Nettoyage
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Événement de chargement du simulateur (une fois au montage)
  useEffect(() => {
    trackSimulatorLoaded({
      profession: initialUrlState.profession.id,
      territory: initialUrlState.territoryId,
      grade: initialUrlState.gradeId,
      echelon: initialUrlState.parsedEchelon.echelonNum,
      embedMode: isEmbedMode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mettre à jour la profession sélectionnée quand l'URL change
  useEffect(() => {
    console.log('🔍 App - useEffect triggered, URL params:', { professionId, territoryId, gradeId, echelonId });
    console.log('🔍 App - Current selectedProfession:', selectedProfession.id);
    console.log('🔍 App - Current selectedOverseasTerritory:', selectedOverseasTerritory);
    console.log('🔍 App - Current selectedGrade:', selectedGrade);
    console.log('🔍 App - Current selectedEchelon:', selectedEchelon);
    console.log('🔍 App - grades available:', grades?.length || 0, 'grades');
    console.log('🔍 App - grades data:', grades?.map(g => ({
      id: g.id,
      nom: g.nom,
      echelonsCount: g.echelons.length,
      echelons: g.echelons.map(e => e.echelon)
    })));
    
    // Parse URL parameters to get profession and territory
    const { 
      profession: urlProfession, 
      territoryId: urlTerritoryId, 
      gradeId: urlGradeId, 
      parsedEchelon: urlParsedEchelon 
    } = parseUrlParams(professionId, territoryId, gradeId, echelonId);
    console.log('🔍 App - Parsed from URL:', { 
      profession: urlProfession.id, 
      territoryId: urlTerritoryId, 
      gradeId: urlGradeId, 
      echelon: urlParsedEchelon 
    });

    // Check if profession or territory changed
    const professionChanged = selectedProfession.id !== urlProfession.id;
    const territoryChanged = selectedOverseasTerritory !== urlTerritoryId;
    const gradeChanged = selectedGrade !== urlGradeId;
    const echelonChanged = selectedEchelon !== urlParsedEchelon.echelonNum;
    
    if (professionChanged || territoryChanged || gradeChanged || echelonChanged) {
      console.log('🔍 App - Changes detected:', { professionChanged, territoryChanged, gradeChanged, echelonChanged });
      
      if (professionChanged) {
        console.log('🔍 App - Setting new profession:', urlProfession.id);
        setSelectedProfession(urlProfession);
        // Reset grade and echelon when changing profession
        setSelectedGrade(urlGradeId);
        setSelectedEchelon(urlParsedEchelon.echelonNum);
        // Reset primes data when changing profession
        setPrimesData({
          primes: [],
          totalMensuel: 0
        });
      }
      
      if (territoryChanged) {
        console.log('🔍 App - Setting new territory:', urlTerritoryId);
        setSelectedOverseasTerritory(urlTerritoryId);
      }
      
      if (gradeChanged) {
        console.log('🔍 App - Setting new grade:', urlGradeId);
        setSelectedGrade(urlGradeId);
      }
      
      if (echelonChanged) {
        console.log('🔍 App - Setting new echelon:', urlParsedEchelon.echelonNum);
        setSelectedEchelon(urlParsedEchelon.echelonNum);
      }
      
      // If the current URL doesn't match the expected canonical URL, redirect
      // Find the actual echelon data for URL generation
      const urlEchelonData = urlGradeId !== null && urlParsedEchelon.echelonNum !== null && grades.length > 0
        ? grades.find(g => g.id === urlGradeId)?.echelons.find(e => e.echelon === urlParsedEchelon.echelonNum)
        : null;
      
      const expectedPath = getSimulatorPath(urlProfession, urlTerritoryId, urlGradeId, urlEchelonData);
      const currentPath = window.location.pathname;
      console.log('🔍 App - URL validation:', { expectedPath, currentPath });
      
      if (currentPath !== expectedPath) {
        console.log('🔍 App - Redirecting to canonical URL:', expectedPath);
        navigate(expectedPath, { replace: true });
      }
    }
  }, [professionId, territoryId, gradeId, echelonId, navigate, selectedProfession.id, selectedOverseasTerritory, selectedGrade, selectedEchelon, grades]);

  // Update primesData when Supabase primes are loaded
  useEffect(() => {
    if (supabasePrimes.length > 0) {
      setPrimesData({
        primes: supabasePrimes,
        totalMensuel: 0 // Will be calculated by usePrimesManagement
      });
    }
  }, [supabasePrimes]);

  // Debug logs for App component state (after useState declarations)
  console.log('🔍 App - Component state:');
  console.log('🔍 App - selectedProfession:', selectedProfession);
  console.log('🔍 App - selectedGrade:', selectedGrade);
  console.log('🔍 App - selectedEchelon:', selectedEchelon);
  console.log('🔍 App - grades loaded:', grades?.length || 0, 'grades');
  console.log('🔍 App - grades data:', grades);
  console.log('🔍 App - selectedProfession.supabaseTable:', selectedProfession.supabaseTable);
  console.log('🔍 App - useGrilleData hook result:', { 
    gradesCount: grades?.length || 0, 
    firstGrade: grades?.[0],
    isLoading: false // We don't have access to isLoading here, but grades array tells us it's loaded
  });

  // Données d'activité basées sur la profession sélectionnée
  const activity: ActivityData = useMemo(() => ({
    profession: selectedProfession,
    actsPerWeekMin: Math.round(selectedProfession.averageActsPerWeek * 0.8),
    actsPerWeekMax: Math.round(selectedProfession.averageActsPerWeek * 1.2),
    averageConsultationPrice: selectedProfession.averageConsultationPrice
  }), [selectedProfession]);

  // Données de statut (fixes pour salarié)
  const [status, setStatus] = useState<StatusData>({
    type: 'titulaire',
    retrocessionPercentage: 0,
    retrocessionCap: null,
    fiscalRegime: 'reel'
  });

  // Gestionnaire pour le changement de profession
  const handleProfessionChange = (profession: Profession) => {
    console.log('🔍 App - handleProfessionChange called with:', profession);
    console.log('🔍 App - Current selectedProfession before change:', selectedProfession.id);
    console.log('🔍 App - Current state:', { selectedOverseasTerritory, selectedGrade, selectedEchelon });
    
    // When changing profession, preserve grade/echelon if they exist in the new profession
    // Otherwise, clear them
    const targetPath = getSimulatorPath(profession, selectedOverseasTerritory, selectedGrade, 
      selectedGrade && selectedEchelon ? grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null
    );
    console.log('🔍 App - targetPath calculated:', targetPath);
    console.log('🔍 App - current location:', window.location.pathname);
    
    // Only navigate if we're not already on the target path
    if (window.location.pathname !== targetPath) {
      console.log('🔍 App - Navigating to:', targetPath);
      navigate(targetPath);
    } else {
      console.log('🔍 App - Already on target path, no navigation needed');
    }

    trackProfessionSelected(profession.id);
  };

  // Gestion des changements
  const handleGradeEchelonChange = (grade: number, echelon: number) => {
    console.log('🔍 App - handleGradeEchelonChange called with:', { grade, echelon });
    console.log('🔍 App - Current state:', { selectedProfession: selectedProfession.id, selectedOverseasTerritory, selectedGrade, selectedEchelon });
    
    // Find the echelon data for URL generation
    const targetGrade = grades.find(g => g.id === grade);
    const targetEchelon = targetGrade?.echelons.find(e => e.echelon === echelon);
    
    console.log('🔍 App - Target grade/echelon data:', { targetGrade: targetGrade?.nom, targetEchelon });
    
    if (!targetEchelon) {
      console.warn('🔍 App - Invalid grade/echelon combination:', { grade, echelon });
      return;
    }
    
    // Generate new URL with grade and echelon
    const targetPath = getSimulatorPath(selectedProfession, selectedOverseasTerritory, grade, targetEchelon);
    console.log('🔍 App - handleGradeEchelonChange targetPath:', targetPath);
    console.log('🔍 App - current location:', window.location.pathname);
    
    // Only navigate if we're not already on the target path
    if (window.location.pathname !== targetPath) {
      console.log('🔍 App - Navigating to:', targetPath);
      navigate(targetPath);
    } else {
      console.log('🔍 App - Already on target path, no navigation needed');
    }
    
    markActivityModified();
    
    // Track user interaction for analytics (only if consent given)
    trackSimulatorInteraction('grade_echelon_selected', {
      grade,
      echelon,
      profession: selectedProfession.id,
    });
    trackGradeSelected(grade, targetGrade?.nom);
    trackEchelonSelected(echelon, grade);
  };

  const handleWorkTimeChange = (percentage: number) => {
    setIsLoadingPrimes(true);
    setIsLoadingRevenue(true);
    
    setWorkTimePercentage(percentage);
    markActivityModified();
    
    // Track user interaction for analytics (only if consent given)
    trackSimulatorInteraction('work_time_changed', { 
      percentage, 
      profession: selectedProfession.id 
    });
    
    // Simulate loading delay
    setTimeout(() => {
      setIsLoadingPrimes(false);
      setIsLoadingRevenue(false);
    }, 500);
  };

  const handleOverseasTerritoryChange = (territory: OverseasTerritoryId | undefined) => {
    console.log('🔍 App - handleOverseasTerritoryChange called with:', territory);
    console.log('🔍 App - Current state:', { selectedProfession: selectedProfession.id, selectedOverseasTerritory, selectedGrade, selectedEchelon });
    
    // Preserve grade/echelon when changing territory
    const targetEchelon = selectedGrade !== null && selectedEchelon !== null ? 
      grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null;
    
    const targetPath = getSimulatorPath(selectedProfession, territory, selectedGrade, targetEchelon);
    console.log('🔍 App - targetPath calculated for territory change:', targetPath);
    console.log('🔍 App - current location:', window.location.pathname);
    
    // Only navigate if we're not already on the target path
    if (window.location.pathname !== targetPath) {
      console.log('🔍 App - Navigating to:', targetPath);
      navigate(targetPath);
    } else {
      console.log('🔍 App - Already on target path, no navigation needed');
    }
    
    markActivityModified();
    
    // Track user interaction for analytics (only if consent given)
    trackSimulatorInteraction('territory_selected', {
      territory,
      profession: selectedProfession.id,
    });
    if (territory) trackTerritorySelected(territory);
  };

  const handlePrimesChange = (newPrimesData: PrimesData) => {
    setIsLoadingRevenue(true);

    // Détecter les primes dont l'état actif a changé pour tracker individuellement
    const previousActiveById = new Map(primesData.primes.map(p => [p.id, p.active]));
    newPrimesData.primes.forEach(p => {
      const prev = previousActiveById.get(p.id);
      if (prev !== undefined && prev !== p.active) {
        trackPrimeToggled(p.id, p.active);
      }
    });

    setPrimesData(newPrimesData);

    // Track prime changes for analytics (only if consent given)
    const activePrimes = newPrimesData.primes.filter(p => p.active);
    trackSimulatorInteraction('primes_updated', {
      activePrimesCount: activePrimes.length,
      totalAmount: newPrimesData.totalMensuel,
      profession: selectedProfession.id,
    });
    
    // Simulate calculation delay
    setTimeout(() => {
      setIsLoadingRevenue(false);
    }, 300);
  };

  // // Auto-collapse activity section when grade/echelon selected
  // useEffect(() => {
  //   if (selectedGrade && selectedEchelon > 0) {
  //     setActivityCollapsed(true);
  //   }
  // }, [selectedGrade, selectedEchelon]);

  // Gestionnaires pour le consentement aux cookies
  const handleCookieAccept = () => {
    console.log('User accepted cookies - initializing Google Analytics');
    initializeGoogleAnalytics();

    setTimeout(() => {
      trackSimulatorInteraction('cookie_consent', { action: 'accepted' });
      trackCookieConsent('accepted');
    }, 1000);
  };

  const handleCookieDecline = () => {
    console.log('User declined cookies - Google Analytics will not be loaded');
    disableGoogleAnalytics();
    trackCookieConsent('declined');
  };

  // Wizard validation par étape
  const stepValidity = useMemo(() => {
    const step0 = selectedOverseasTerritory !== undefined;
    const currentGrade = selectedGrade ? grades.find(g => g.id === selectedGrade) : null;
    const currentEchelonData = currentGrade && selectedEchelon !== null
      ? currentGrade.echelons.find(e => e.echelon === selectedEchelon)
      : null;
    const step1 = step0 && selectedGrade !== null && currentEchelonData !== undefined && currentEchelonData !== null;
    const step2 = step1 && userInteractions.hasModifiedPrimes;
    return [step0, step1, step2, true];
  }, [selectedOverseasTerritory, selectedGrade, selectedEchelon, grades, userInteractions.hasModifiedPrimes]);

  const stepHelperMessages: (string | undefined)[] = [
    'Renseigne ton territoire pour continuer.',
    'Sélectionne ton grade et échelon pour continuer.',
  
    undefined,
  ];

  const canGoNext = stepValidity[currentStep];
  const canGoPrev = currentStep > 0;
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  const handleGoToStep = (stepId: number) => {
    if (stepId <= maxReachedStep) {
      setCurrentStep(stepId);
      scrollToWizard();
    }
  };

  const handleNextStep = () => {
    if (!canGoNext || isLastStep) return;
    const previousStep = currentStep;
    const nextStep = currentStep + 1;
    const durationSeconds = Math.round((Date.now() - stepEnteredAtRef.current) / 1000);
    trackWizardStepCompleted(
      previousStep,
      WIZARD_STEPS[previousStep]?.label ?? String(previousStep),
      durationSeconds,
    );
    setCurrentStep(nextStep);
    setMaxReachedStep(prev => Math.max(prev, nextStep));
    trackSimulatorInteraction('wizard_step_changed', {
      step: nextStep,
      profession: selectedProfession.id,
    });
    scrollToWizard();
  };

  const handlePrevStep = () => {
    if (!canGoPrev) return;
    const previousStep = currentStep;
    trackWizardBackClicked(
      previousStep,
      WIZARD_STEPS[previousStep]?.label ?? String(previousStep),
    );
    setCurrentStep(prev => prev - 1);
    scrollToWizard();
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setMaxReachedStep(0);
    scrollToWizard();
  };

  useWizardSession({
    professionId: selectedProfession.id,
    currentStep,
    maxReachedStep,
    totalSteps: WIZARD_STEPS.length,
  });

  const territoryData = selectedOverseasTerritory
    ? findOverseasTerritoryById(selectedOverseasTerritory)
    : null;
  const currentGradeData = selectedGrade ? grades.find(g => g.id === selectedGrade) : null;
  const currentEchelonData = currentGradeData && selectedEchelon !== null
    ? currentGradeData.echelons.find(e => e.echelon === selectedEchelon)
    : null;

  const previewSalary = useMemo(() => {
    if (!selectedGrade || selectedEchelon === null || grades.length === 0) return null;
    return calculateSalary(
      selectedGrade,
      selectedEchelon,
      workTimePercentage,
      grades,
      primesData,
      selectedOverseasTerritory ?? null,
      { cotisations },
    );
  }, [selectedGrade, selectedEchelon, workTimePercentage, primesData, cotisations, grades, selectedOverseasTerritory]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EBF5F4' }} role="document">
      <ErrorBoundary>
        <SEOStructuredData 
          isEmbedMode={isEmbedMode} 
          selectedProfession={selectedProfession}
          selectedOverseasTerritory={selectedOverseasTerritory ? findOverseasTerritoryById(selectedOverseasTerritory) : null}
          selectedGrade={selectedGrade ? grades.find(g => g.id === selectedGrade) : null}
          selectedEchelon={selectedGrade && selectedEchelon !== null ? grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null}
        />
        
        {/* Header banner - ARIA landmark */}
        <header role="banner" aria-label="En-tête du simulateur de salaire">
          <EmbedHeader isEmbedMode={isEmbedMode} />
        </header>
        
        {/* Main content area - ARIA landmark */}
        <main role="main" aria-label="Simulateur de salaire" className={`container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl ${isMobile ? 'mt-16' : ''}`}>
          {/* En-tête principal */}
          <header className="text-center mb-3 sm:mb-8 pt-1 md:pt-0">
            {(() => {
              const gradeData = selectedGrade ? grades.find(g => g.id === selectedGrade) : null;
              const echelonData = gradeData && selectedEchelon !== null
                ? gradeData.echelons.find(e => e.echelon === selectedEchelon)
                : null;
              const territoryData = selectedOverseasTerritory
                ? findOverseasTerritoryById(selectedOverseasTerritory)
                : null;

              const titleParts = [`Simulateur de Salaire ${selectedProfession.name}`];
              if (gradeData && echelonData) {
                titleParts.push(`Grade ${gradeData.id}, Échelon ${echelonData.echelon}`);
              }
              if (territoryData?.id) {
                titleParts.push(`en ${territoryData.name}`);
              }

              const subtitleParts = [`Calcul Salaire ${selectedProfession.name}`];
              if (gradeData) subtitleParts.push(gradeData.nom);
              if (territoryData?.id) subtitleParts.push(territoryData.name);
              subtitleParts.push('Grille Indiciaire et Primes 2025');

              const descParts = [`Simulateur officiel pour calculer votre salaire de ${selectedProfession.name.toLowerCase()}`];
              if (gradeData && echelonData) {
                descParts.push(`au ${gradeData.nom}, Échelon ${echelonData.echelon}`);
              }
              if (territoryData?.id) descParts.push(`en ${territoryData.name}`);
              descParts.push('dans la fonction publique hospitalière');

              return (
                <>
                  <h1 className="text-lg sm:text-3xl md:text-4xl font-bold text-gray-800 break-words leading-tight text-center mb-2 sm:mb-4">
                    <span className="sm:hidden">{titleParts.join(' - ')}</span>
                    <span className="hidden sm:inline">
                      {titleParts.join(' - ')}<br />
                      Fonction Publique Hospitalière
                    </span>
                  </h1>

                  <div className={`${isSeoContextOpen ? 'block' : 'hidden'} sm:block`}>
                    <h2 className="text-base sm:text-lg text-gray-700 mb-2 px-2 font-semibold">
                      {subtitleParts.join(' - ')}
                      <span className="sm:hidden"> — Fonction Publique Hospitalière</span>
                    </h2>
                    <p className="text-sm text-gray-600 px-2">
                      {descParts.join(' ')}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsSeoContextOpen(prev => !prev)}
                    aria-expanded={isSeoContextOpen}
                    aria-label={isSeoContextOpen ? 'Masquer le contexte de la simulation' : 'Voir le contexte de la simulation'}
                    className="sm:hidden inline-flex items-center gap-1 mt-1 text-xs text-emerald-700 font-medium hover:text-emerald-800"
                  >
                    {isSeoContextOpen ? 'Masquer le contexte' : 'Voir le contexte de la simulation'}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isSeoContextOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* CTA Calendrier de Paye - desktop uniquement, mobile disponible en bas de page */}
                  <div className="mt-6 justify-center hidden md:flex">
                    <a
                      href="/paie-fonctionnaire"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-appines-darker to-appines-darker text-white font-semibold rounded-lg shadow-lg hover:from-appines-darker hover:to-appines-dark hover:shadow-xl hover:scale-105 transition-all duration-200"
                    >
                      Voir le calendrier de paye des infirmières
                      <span className="ml-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                        Nouveau
                      </span>
                    </a>
                  </div>

                  {/* Bannière Lead Gen App'Ines - Placée sous la description */}
                  <div className="mt-6 md:block hidden">
                    <AppinesBanner professionId={selectedProfession.id} />
                  </div>
                </>
              );
            })()}
          </header>

          {/* Bannière App'Ines sticky sur mobile */}
          <AppinesBanner 
            professionId={selectedProfession.id}
            className="md:hidden"
          />

          {/* Wizard — Parcours guidé en 4 étapes */}
          <section
            ref={wizardSectionRef}
            aria-label="Simulateur en 4 étapes"
            className="scroll-mt-4 space-y-3 sm:space-y-4 pb-28 md:pb-0"
          >
            <WizardStepper
              steps={WIZARD_STEPS}
              currentStep={currentStep}
              maxReachedStep={maxReachedStep}
              onStepClick={handleGoToStep}
              isMobile={isMobile}
            />

            <div className={`grid grid-cols-1 gap-3 lg:gap-4 items-start ${isLastStep ? '' : 'lg:grid-cols-3'}`}>
              <div className={isLastStep ? '' : 'lg:col-span-2'}>
                <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-4 lg:p-5">
                  {currentStep === 0 && (
                    <>
                      <div className="mb-3 hidden sm:block">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ton activité</h2>
                        <p className="text-xs text-gray-600">
                          Renseigne ta profession, ton statut, ton territoire et ton temps de travail.
                        </p>
                      </div>
                      <ErrorBoundary fallback={
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                          <p className="text-red-800">Erreur dans la section de configuration</p>
                        </div>
                      }>
                        <ActivityDetails
                          activity={activity}
                          onActivityChange={() => {}}
                          onMarkActivityModified={markActivityModified}
                          onProfessionSelect={handleProfessionChange}
                          status={status}
                          onStatusChange={(newStatus) => {
                            if (newStatus.type !== status.type) {
                              trackStatusSelected(newStatus.type);
                            }
                            setStatus(newStatus);
                          }}
                          workTimePercentage={workTimePercentage}
                          onWorkTimeChange={handleWorkTimeChange}
                          selectedOverseasTerritory={selectedOverseasTerritory}
                          onOverseasTerritoryChange={handleOverseasTerritoryChange}
                          selectedProfession={selectedProfession}
                        />
                      </ErrorBoundary>
                    </>
                  )}

                  {currentStep === 1 && (
                    <>
                      <div className="mb-3 hidden sm:block">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ta grille indiciaire</h2>
                        <p className="text-xs text-gray-600">
                          Sélectionne ton grade et ton échelon pour déterminer ton salaire de base.
                        </p>
                      </div>
                      {selectedOverseasTerritory !== undefined ? (
                        <ErrorBoundary fallback={
                          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                            <p className="text-red-800">Erreur dans la grille salariale</p>
                          </div>
                        }>
                          <RemunerationIndiciaire
                            selectedGrade={selectedGrade}
                            selectedEchelon={selectedEchelon}
                            onGradeEchelonChange={handleGradeEchelonChange}
                            selectedProfession={selectedProfession}
                          />
                        </ErrorBoundary>
                      ) : (
                        <div className="p-5 bg-yellow-50 rounded-xl border border-yellow-200">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h3 className="font-semibold text-yellow-900 mb-1">Territoire d'affectation requis</h3>
                              <p className="text-sm text-yellow-800">
                                Reviens à l'étape précédente pour sélectionner ton territoire avant de choisir ton grade et ton échelon.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {currentStep === 2 && (
                    <>
                      <div className="mb-3 hidden sm:block">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Tes primes & indemnités</h2>
                        <p className="text-xs text-gray-600">
                          Active et configure les primes qui s'appliquent à ta situation.
                        </p>
                      </div>
                      <ErrorBoundary fallback={
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                          <p className="text-red-800">Erreur dans les primes et indemnités</p>
                          {errorPrimes && (
                            <p className="text-red-600 text-sm mt-2">{errorPrimes}</p>
                          )}
                        </div>
                      }>
                        <PrimesIndemnites
                          primesData={primesData}
                          onPrimesChange={handlePrimesChange}
                          onMarkPrimesModified={markPrimesModified}
                          workTimePercentage={workTimePercentage}
                          selectedGrade={selectedGrade}
                          selectedEchelon={selectedEchelon}
                          currentProfessionId={selectedProfession.id}
                          selectedStatus={status.type}
                          isLoading={isLoadingPrimes}
                          grades={grades}
                          selectedProfession={selectedProfession}
                        />
                      </ErrorBoundary>
                    </>
                  )}

                  {currentStep === 3 && (
                    <>
                      <div className="mb-3 hidden sm:block">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ton estimation</h2>
                        <p className="text-xs text-gray-600">
                          Voici ton revenu estimé en fonction des informations renseignées.
                        </p>
                      </div>
                      <ErrorBoundary fallback={
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                          <p className="text-red-800">Erreur dans le calcul des revenus</p>
                        </div>
                      }>
                        <RevenueCalculator
                          id="revenue-calculator"
                          calculation={null}
                          selectedGrade={selectedGrade}
                          selectedEchelon={selectedEchelon}
                          workTimePercentage={workTimePercentage}
                          selectedOverseasTerritory={selectedOverseasTerritory}
                          primesData={primesData}
                          cotisationsData={{ cotisations, totalMensuel: 0, totalAnnuel: 0 }}
                          grades={grades}
                          isLoadingCotisations={isLoadingCotisations}
                          selectedProfession={selectedProfession}
                        />
                      </ErrorBoundary>
                    </>
                  )}

                  {!isMobile && (
                    <WizardNavigation
                      canGoPrev={canGoPrev}
                      canGoNext={canGoNext}
                      isLastStep={isLastStep}
                      helperMessage={!canGoNext && !isLastStep ? stepHelperMessages[currentStep] : undefined}
                      onPrev={handlePrevStep}
                      onNext={handleNextStep}
                      onRestart={handleRestart}
                      isMobile={false}
                    />
                  )}
                </div>
              </div>

              {!isMobile && !isLastStep && (
                <aside
                  role="complementary"
                  aria-label="Aperçu de l'estimation"
                  className="lg:col-span-1"
                >
                  <EstimationPreview
                    profession={selectedProfession}
                    status={status}
                    territory={territoryData ?? null}
                    gradeLabel={currentGradeData?.nom ?? null}
                    echelonLabel={currentEchelonData?.echelonLabel ?? (currentEchelonData ? `Échelon ${currentEchelonData.echelon}` : null)}
                    primesData={primesData}
                    brutMonthly={previewSalary?.withPrimesMonthly ?? null}
                    netEstimatedMonthly={previewSalary?.netAfterTaxMonthly ?? null}
                  />
                  {currentStep === 2 && (
                    <AppinesHiringBonus professionId={selectedProfession.id} variant="sidebar" />
                  )}
                </aside>
              )}
            </div>
          </section>

          {/* Informations complémentaires - Section secondaire (étape Résultats uniquement) */}
          <section role="region" aria-label="Informations complémentaires" className={`mt-6 sm:mt-8 space-y-6 ${currentStep === WIZARD_STEPS.length - 1 ? '' : 'hidden'}`}>
            {/* 1. Tableau comparatif des salaires */}
            {grades.length > 0 && (
              <ErrorBoundary fallback={
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <p className="text-red-800">Erreur dans le tableau comparatif</p>
                </div>
              }>
                <React.Suspense fallback={<LoadingSpinner size="sm" text="Chargement du tableau..." />}>
                  <SalaryComparisonTable
                    selectedProfession={selectedProfession}
                    selectedOverseasTerritory={selectedOverseasTerritory ? findOverseasTerritoryById(selectedOverseasTerritory) : null}
                    selectedGrade={selectedGrade ? grades.find(g => g.id === selectedGrade) : null}
                    selectedEchelon={selectedGrade && selectedEchelon !== null ? grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null}
                    grades={grades}
                  />
                </React.Suspense>
              </ErrorBoundary>
            )}

            {/* 2. Contenu contextuel pour différenciation sémantique */}
            <ErrorBoundary fallback={
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800">Erreur dans le contenu contextuel</p>
              </div>
            }>
              <React.Suspense fallback={<LoadingSpinner size="sm" text="Chargement du contenu..." />}>
                <ProfessionContextualContent
                  selectedProfession={selectedProfession}
                  selectedOverseasTerritory={selectedOverseasTerritory ? findOverseasTerritoryById(selectedOverseasTerritory) : null}
                  selectedGrade={selectedGrade ? grades.find(g => g.id === selectedGrade) : null}
                  selectedEchelon={selectedGrade && selectedEchelon !== null ? grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null}
                  grades={grades}
                />
              </React.Suspense>
            </ErrorBoundary>

            {/* 3. Articles recommandés */}
            <ErrorBoundary fallback={
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800">Erreur dans les articles</p>
              </div>
            }>
              <React.Suspense fallback={<LoadingSpinner size="sm" text="Chargement des articles..." />}>
                <ArticlesCarousel profession={activity.profession} />
              </React.Suspense>
            </ErrorBoundary>

            {/* 4. FAQ spécifique à la profession */}
            <ErrorBoundary fallback={
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800">Erreur dans la FAQ</p>
              </div>
            }>
              <React.Suspense fallback={<LoadingSpinner size="sm" text="Chargement de la FAQ..." />}>
                <ProfessionFAQ
                  selectedProfession={selectedProfession}
                  selectedOverseasTerritory={selectedOverseasTerritory ? findOverseasTerritoryById(selectedOverseasTerritory) : null}
                  selectedGrade={selectedGrade ? grades.find(g => g.id === selectedGrade) : null}
                  selectedEchelon={selectedGrade && selectedEchelon !== null ? grades.find(g => g.id === selectedGrade)?.echelons.find(e => e.echelon === selectedEchelon) : null}
                />
              </React.Suspense>
            </ErrorBoundary>
          </section>
        </main>

        {/* Prime App'Ines sticky mobile (étape Primes uniquement) */}
        {isMobile && currentStep === 2 && (
          <div
            className="fixed left-0 right-0 z-40"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 72px)' }}
          >
            <AppinesHiringBonus professionId={selectedProfession.id} variant="mobile-sticky" />
          </div>
        )}

        {/* Navigation wizard sticky (mobile uniquement) */}
        {isMobile && (
          <WizardNavigation
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            isLastStep={isLastStep}
            helperMessage={!canGoNext && !isLastStep ? stepHelperMessages[currentStep] : undefined}
            onPrev={handlePrevStep}
            onNext={handleNextStep}
            onRestart={handleRestart}
            isMobile={true}
          />
        )}

        <Footer />

        {/* Bannière de consentement aux cookies */}
        <CookieConsentBanner
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
        />
      </ErrorBoundary>
    </div>
  );
}